import { useState } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, Spinner } from '@chakra-ui/react';
import { Button } from '@mercator-gcp/grout';
import { PencilIconButton } from 'components/elements/Button/PencilIconButton';
import { FlexRow } from 'components/layout/FlexRow';
import { EditAccountDetails } from 'components/modals/EditAccountDetails';
import { InviteNewUsersModal } from 'components/modals/InviteNewUsers';
import { AccountSettingsModule } from 'components/widgets/AccountSettings/layout/AccountSettingsModule';
import { AccountSettingsSection } from 'components/widgets/AccountSettings/layout/AccountSettingsSection';
import useOrganizationUsers from 'hooks/useOrganizationUsers';
import useUserAccount from 'hooks/useUserAccount';
import { trackInviteNewUser } from 'utils/analytics';
import { InviteNewUserActions } from 'utils/analytics/constants';
import { formatDate } from 'utils/date';

const INVITE_NEW_USER_BUTTON_TEXT = '+ Invite new users';

const stickyHeaderStyle = {
  '& th': {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    bg: 'white',
  },
};

export const Users = () => {
  const { fetchUser, selectedUser } = useUserAccount();
  const { organizationUsers, refetchingUsers } = useOrganizationUsers();
  const [isUserModalOpen, setIsUserModalOpen] = useState<boolean>(false);
  const [isAccountDetailsModalOpen, setIsAccountDetailsModalOpen] =
    useState<boolean>(false);

  return (
    <AccountSettingsSection title="Users">
      <AccountSettingsModule
        title="Manage Users"
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              trackInviteNewUser({
                clickAction: InviteNewUserActions.NEW_USER,
                clickText: INVITE_NEW_USER_BUTTON_TEXT,
              });
              setIsUserModalOpen(true);
            }}
          >
            {INVITE_NEW_USER_BUTTON_TEXT}
          </Button>
        }
      >
        <Box sx={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
          {organizationUsers.length > 0 && !refetchingUsers ? (
            <Table size="sm">
              <Thead sx={stickyHeaderStyle}>
                <Tr>
                  <Th>Account email</Th>
                  <Th>Status</Th>
                  <Th>Last Login</Th>
                  <Th>Job Title</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {organizationUsers.map((user) => (
                  <Tr key={`organization-user-${user.id}`}>
                    <Td>{user.email}</Td>
                    <Td>{user.is_active ? 'Active' : 'Inactive'}</Td>
                    <Td>{formatDate(user.last_login) || '-'}</Td>
                    <Td>{user.job_title ? user.job_title : '-'}</Td>
                    <Td>
                      <PencilIconButton
                        handleClick={() => {
                          fetchUser(user.id);
                          setIsAccountDetailsModalOpen(true);
                        }}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <FlexRow sx={{ justifyContent: 'center' }}>
              <Spinner sx={{ color: 'brand.army-green' }} />
            </FlexRow>
          )}
        </Box>
      </AccountSettingsModule>

      <InviteNewUsersModal
        isOpen={isUserModalOpen}
        onInvite={(clickText?: string) => {
          trackInviteNewUser({ clickAction: InviteNewUserActions.INVITE, clickText });
        }}
        onCancel={(clickText?: string) => {
          trackInviteNewUser({ clickAction: InviteNewUserActions.CANCEL, clickText });
          setIsUserModalOpen(false);
        }}
        onClose={() => {
          setIsUserModalOpen(false);
        }}
      />
      <EditAccountDetails
        isOpen={isAccountDetailsModalOpen}
        onClose={() => {
          setIsAccountDetailsModalOpen(false);
        }}
        userData={selectedUser}
        setModalOpen={setIsAccountDetailsModalOpen}
      />
    </AccountSettingsSection>
  );
};
